import React, {useCallback} from 'react';

import ExcelJS from 'exceljs';
import {saveAs} from 'file-saver';
import {useUserEmail} from 'src/hooks/useUserEmail';
import {getUser} from 'src/modules/admin/services';
import {printSheetHeader, printTableData, printTableDataHeader} from 'src/modules/change-requests/download/print-data';
import {Filters} from 'src/modules/change-requests/typedefs';
import {Download} from 'src/modules/downloads/download';
import {getUserName} from 'src/modules/downloads/utils/get-user-name';
import {getUsersInfo} from 'src/modules/users-info/services';
import {ChangeRequest} from 'src/openapi/models/ChangeRequest';
import {useAppSelector} from 'src/store';
import {userEmailsToUserNames} from 'src/utils/userEmailsToUserNames';

import {formatDate} from '@pb/kit-formatters';

type Props = {
    filters: Filters,
    changeRequests: Array<ChangeRequest>,
    showOnlyMy: boolean,
};

export const ChangeRequestsExcelDownload = ({filters, changeRequests, showOnlyMy}: Props) => {
    const usersInfo = useAppSelector(getUsersInfo);
    const user = useAppSelector(getUser);
    const userEmail = useUserEmail();
    const userName = getUserName(userEmail, user);

    const onDownload = useCallback(async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Change Requests', {views: [{showGridLines: false}]});

        const authorNames = showOnlyMy
            ? [userName]
            : userEmailsToUserNames(filters.authorEmail, usersInfo);

        printSheetHeader(worksheet, userName, {...filters, authorEmail: authorNames});
        printTableDataHeader(worksheet);
        printTableData(worksheet, changeRequests, usersInfo);

        const buffer = await workbook.xlsx.writeBuffer();
        const date = formatDate(new Date(), 'YYYY_DD_MM_H_mm_ss');

        saveAs(new Blob([buffer]), `UD_Design_Tool_Change_Requests_${date}.xlsx`);
    }, [showOnlyMy, userName, filters, usersInfo, changeRequests]);

    return (
        <Download onDownload={onDownload}/>
    );
};
